import { Component, OnInit, OnDestroy, Input, Optional, Inject } from '@angular/core';
import { Holdings, HoldingsTotal } from './../../../models/common/trading-holdings';
import { Account } from './../../../models/common/trading-common.model';
import { TradingService } from './../../../services/common/trading-service.service';
import { Subscription } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';
import { WeeklyIndexUnderliers } from '../../../models/option/trading-option.model';

@Component({
    selector: 'holding-component',
    templateUrl: './holding.component.html',
    styleUrls: ['./holding.component.css']
})

/**
 * The Holdings component to take care of rendering the Holdings section for all asset types.
 */
export class HoldingComponent implements OnInit, OnDestroy {

    symIds: string[] = [];
    @Input() platform: string;
    @Input() isMobileDialog: boolean = false;
    @Input() isForSecondSymbol: boolean = false;

    holdings: Holdings[];
    filteredHolding: Holdings[];

    underlierHoldings: Holdings[] = [];
    hasUnderlierHoldings: boolean;

    callHoldings: Holdings[] = [];
    hasCallHoldings: boolean;

    putHoldings: Holdings[] = [];
    hasPutHoldings: boolean;

    valueCurrency: string;
    totalHolding = {} as HoldingsTotal;

    isAvgPriceOrAllocationAcct: boolean;
    showTotal: boolean;
    showMktValInPositionCcy: boolean = false;
    holdingsError: boolean;
    activeTheme: string;
    isRetail: boolean;
    language: string = '';


    private accountSub: Subscription;
    private holdingSub: Subscription;
    private holdingErrorSub: Subscription;
    private symbolSub: Subscription;

    constructor(
        private tradingService: TradingService,
        @Optional() @Inject('AppSettings') appSettings: any) {
        // TODO: plaform should come from portal contextService. Getting from portal appsettings until contextService emits platform
        if (appSettings) {
            this.platform = appSettings.platform;
            if (appSettings?.language) {
                this.language = appSettings.language;
                if (this.language === 'es') {
                   registerLocaleData( es );
                }
            }
        }
    }

    ngOnInit() {
        this.isRetail = this.platform === 'retail';
        this.accountSub = this.tradingService.accountSubject$.subscribe((data: Account) => this.onAccount(data));

        const theSubject = this.isForSecondSymbol ? this.tradingService.secondSymbolSubject$ : this.tradingService.symbolSubject$;
        this.symbolSub = theSubject.subscribe((symId: string[]) => {
            if (!!symId) {
                this.onSymSelected(symId);
            } else {
                this.clearHoldingsData();
            }
        });
    }

    private onAccount(account: Account): void {
        this.activeTheme = this.tradingService.getActiveThemeFromCurrentContainer();
        if (!!account && account.acctNum) {
            this.isAvgPriceOrAllocationAcct =
            account.acctNum.substring(0, 3) === '950' || account.acctNum.substring(3, 6) === '897' || account.acctNum.substring(3, 6) === '896';
            this.clearHoldingsData();

            if ( !!this.holdingSub ) {
                this.holdingSub.unsubscribe();
            }

            this.holdingSub = this.tradingService.holdingSubject$.subscribe( (data: Holdings[]) => this.updateCurrentHoldings(data) );

            if ( !!this.holdingErrorSub ) {
                this.holdingErrorSub.unsubscribe();
            }

            this.holdingErrorSub = this.tradingService.holdingErrorSubject$.subscribe( (err) => this.holdingsErrorCallback(err));
        }
    }

    private updateCurrentHoldings(holdingsData: Holdings[]): void {
        this.holdings = holdingsData;
        this.holdingsError = false;

        if ( this.symIds.length > 0 ) {
            this.onSymSelected(this.symIds);
        }
    }

    private holdingsErrorCallback(err): void {
        if ( err ) {
            this.holdingsError = true;
        } else {
            this.holdingsError = false;
        }
    }

    // Invoked when new symbol selected and processes the holdings
    private onSymSelected(selectedSymIds: string[]): void {
        this.symIds = [];
        for ( let symbol of selectedSymIds ) {
            if(symbol) {
                this.symIds.push(symbol.trim().toUpperCase());
            }
        }

        // Have to check if the symbol passed in was a full option symbol. If so,
        // need to trim it down to just the underlyer part
        for ( let i: number = 0; i < this.symIds.length; i++ ) {
            if ( this.symIds[i].indexOf(' C ') > 0 || this.symIds[i].indexOf(' P ') > 0 ) {
                this.symIds[i] = this.symIds[i].substring(0, this.symIds[i].indexOf(' '));
            }
        }

        this.processHoldings();
    }

    // searches for the specified holdings
    // also puts a total on top if at least one holding is found
    processHoldings() {
        if (this.holdings?.length > 0) {
           this.filterHoldings();
           this.updateHoldings();
            // If there's more than one underlierHoldings then add a total row and show total
            if (this.underlierHoldings?.length > 1) {
               this.calculateTotalHolding();
            }
        }
    }

    //find all the holdings for the symbol selected
   private filterHoldings(): void {
        this.filteredHolding = [];
        this.filteredHolding = this.holdings.filter(
            (p) => p &&
                (
                    (!!p.symbol &&
                        (this.symIdContains(p.symbol.trim().toUpperCase()) ||
                            this.symIdContains(p.symbol.substring(0, p.symbol.indexOf(' ')).toUpperCase()))
                    ) || (
                        !!p.cusip &&
                        this.symIdContains(p.cusip.trim().toUpperCase())
                    ) || (
                        !!p.isin &&
                        this.symIdContains(p.isin.trim().toUpperCase())
                    )
                )
        );
    }

    //calculate the total quantity of holdings for the symbol selected
    private calculateTotalHolding(): void {
        let quantity: number = 0;
        let marketValue: number = 0;
        this.totalHolding = {} as HoldingsTotal;
        const underliers = this.underlierHoldings;
        for ( let underlier of underliers ) {
            const marketValueNum = this.showMktValInPositionCcy ? + underlier.marketValuePositionCcy : +underlier.marketValueBaseCcy;
            quantity = quantity + underlier.tradeDateQuantity;
            if (marketValueNum && !isNaN(marketValueNum)) {
                marketValue = marketValue + marketValueNum;
            }
        }
        this.totalHolding.tradeDateQuantity = quantity;
        this.totalHolding.marketValue = marketValue;
        this.showTotal = true;
    }

    private symIdContains(testSecurityID: string): boolean {
        if ( this.symIds.length === 1 && this.symIds[0].indexOf(' ') > 0 && this.symIds[0].indexOf(' C ') === -1 && this.symIds[0].indexOf(' P ') === -1 ) {
            // symIds has an equity symbol with space and no secondary option root version provided; see if the test symbol matches either
            // symIds[0] or that value with the space stripped out. This will ensure we correctly show any option holdings for this equity.
            let baseSym = this.symIds[0];
            let altSym = baseSym.substring(0, baseSym.indexOf(' ')) + baseSym.substring(baseSym.indexOf(' ') + 1);

            return testSecurityID === baseSym || testSecurityID === altSym;
        } else if (this.isAdjustedOrIndexSymbol(testSecurityID)) {
            return true;
        }
        else {
            return testSecurityID === this.symIds[0] || testSecurityID === this.symIds[1];
        }
    }

    // symbol - holding record
    // this.symIds[0] - underlying sym - textbox
    // GLOBOEM-2475 -- This function won't always be passed a symbol, so renaming the input param for clarity
    //                 and adding a check for length of the test ID to rule out CUSIP / ISIN values
    //                 ADDITIONAL CHANGE FOR THIS ON May 11, 2023 -- Do length check of underlyingSymbol so
    //                 weekly index options aren't checked if the value isn't at least 4 characters in length
    isAdjustedOrIndexSymbol(testSecurityID: string): boolean {
        let dataFromSymbol = testSecurityID?.split(' '); // 'AAPL 08192022 C 120' => ['AAPL', '08192022', 'C', '120']
        let underlyingSymbol = dataFromSymbol[0]; //holding sym

        // underlying symbol is a weekly index if it is present in WeeklyIndexUnderliers AND the value from WeeklyIndexUnderliers matches our user-entered symbol.
        // Weekly index positions should be included with the overall filtered position list
        if ( WeeklyIndexUnderliers.hasOwnProperty(underlyingSymbol) && (WeeklyIndexUnderliers[underlyingSymbol] === this.symIds[0]) ) {
            return true;
        }
        // underlying symbol will be considered an adjusted option if the symbol is less than 9 characters long (i.e., not a cusip or or ISIN),
        // starts with the user-entered symbol, and the characters after that evaluate as a number
        else if (underlyingSymbol.startsWith(this.symIds[0]) && underlyingSymbol.length > this.symIds[0].length && underlyingSymbol.length < 9) {
            let adjustedLeg1Val: any = underlyingSymbol.substring(this.symIds[0].length);
            if (!isNaN(adjustedLeg1Val)) {
                return true;
            }
        }
    }

    // clears all data on new symbol selection
    clearHoldingsData(): void {
        this.hasUnderlierHoldings = false;
        this.hasCallHoldings = false;
        this.hasPutHoldings = false;
        this.underlierHoldings = [];
        this.callHoldings = [];
        this.putHoldings = [];
        this.filteredHolding = [];
        this.holdingsError = this.tradingService.holdingErrorSubject$ && this.tradingService.holdingErrorSubject$.value && this.tradingService.holdingErrorSubject$.value.hasError;
        this.symIds = [];
        this.totalHolding = {} as HoldingsTotal;
        this.showTotal = false;
    }

    // checks the current item in holdings list are underLyer/call/puts and add to the list appropriately
    updateHoldings(): void {
        this.callHoldings = [];
        this.putHoldings = [];
        this.underlierHoldings = [];
        this.hasCallHoldings = false;
        this.hasPutHoldings = false;
        this.hasUnderlierHoldings = false;
        for (let holding of this.filteredHolding) {
            if (this._isCall(holding.symbol)) {
                    this.callHoldings.push(holding);
                    this.hasCallHoldings = true;
                    this.showMktValInPositionCcy = false;
                    this.valueCurrency = 'USD';
            } else if (this._isPut(holding.symbol)) {
                    this.putHoldings.push(holding);
                    this.hasPutHoldings = true;
                    this.showMktValInPositionCcy = false;
                    this.valueCurrency = 'USD';
            } else {  // some symbol with space like 'BRK A' is valid underlyer
            this.underlierHoldings.push(holding);
            this.hasUnderlierHoldings = true;

            // symId may not be the symbol -- with mutual funds, theuser may have entered a cusip or ISIN.
            // If the holding has a symbol and it's different from symId, update symId so we preferentially
            // show it in the UI when available.
            if ( !this.symIdContains(holding.symbol) && this.symIds.length === 1 ) {
                this.symIds[0] = holding.symbol;
            }

            if ( holding.positionCcy !== 'USD' ) {
                this.showMktValInPositionCcy = true;
                this.valueCurrency = holding.positionCcy;
            } else {
                this.showMktValInPositionCcy = false;
                this.valueCurrency = 'USD';
            }
            }
        }
        if(this.underlierHoldings.length > 0){
            this.underlierHoldings.sort((a,b)=> a.accountType.localeCompare(b.accountType));
        }
    }
    // Checks if a holding is a call
    _isCall(symbol): boolean {
        return symbol.indexOf(' C ') > 0;
    }
    // Checks if a holding is a put
    _isPut(symbol): boolean {
        return symbol.indexOf(' P ') > 0;
    }
    // Checks if a holding is not an option
    _notOption(symbol): boolean {
        return !this._isCall(symbol) && !this._isPut(symbol);
    }

    // Destroys all the subscription handles
    ngOnDestroy(): void {
        if ( !!this.holdingSub ) {
            this.holdingSub.unsubscribe();
        }

        if ( !!this.holdingErrorSub ) {
            this.holdingErrorSub.unsubscribe();
        }

        if ( !!this.symbolSub ) {
            this.symbolSub.unsubscribe();
        }

        if ( !!this.accountSub ) {
            this.accountSub.unsubscribe();
        }
    }
}
